import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { SHOP } from "../shop";

// ─── Colour tokens ────────────────────────────────────────────────────────────

const BRAND = SHOP.accent;

// ─── Default email texts (mirror CF defaults) ─────────────────────────────────

const DELIVERY_EMAIL_DEFAULTS = {
  fromLocal: "versand",
  subject: "Deine Bestellung #{{ORDER}} ist angekommen! 🎉",
  body:
    "Hallo {{NAME}},\n\n" +
    "wir hoffen, deine Bestellung #{{ORDER}} ist gut bei dir angekommen " +
    "und du freust dich über dein personalisiertes Geschenk!\n\n" +
    "Wir würden uns sehr freuen, wenn du uns eine kurze Bewertung " +
    "hinterlässt — das hilft uns sehr:\n\n" +
    "{{REVIEW_LINK}}\n\n" +
    "Vielen herzlichen Dank und wir freuen uns, dich bald wiederzusehen.\n\n" +
    "Herzliche Grüße\n" +
    "Dein " + SHOP.name + "-Team",
  reviewLinkUrl: "https://www.etsy.com/your/purchases",
  reviewLinkText: "Bewertung hinterlassen →",
  emailFooter: "© " + SHOP.name + " · " + SHOP.email,
};

const PICKUP_EMAIL_DEFAULTS = {
  subject: "Deine Bestellung #{{ORDER}} liegt zur Abholung bereit",
  body:
    "Hallo {{NAME}},\n\n" +
    "deine Bestellung #{{ORDER}} konnte leider nicht zugestellt werden " +
    "und liegt jetzt in einer DHL Filiale zur Abholung bereit.\n\n" +
    "{{LOCATION}}\n\n" +
    "Bitte hole deine Sendung innerhalb von 7 Werktagen ab — " +
    "danach wird sie an uns zurückgeschickt.\n\n" +
    "Herzliche Grüße\n" +
    "Dein " + SHOP.name + "-Team",
  reminderSubject: "Erinnerung: Bestellung #{{ORDER}} wartet noch auf Abholung",
  reminderBody:
    "Hallo {{NAME}},\n\n" +
    "wir möchten dich daran erinnern, dass deine Bestellung #{{ORDER}} " +
    "noch in der DHL Filiale auf dich wartet.\n\n" +
    "{{LOCATION}}\n\n" +
    "Bitte hole sie bald ab, damit sie nicht zurückgeschickt wird.\n\n" +
    "Herzliche Grüße\n" +
    "Dein " + SHOP.name + "-Team",
  reminderDays: 4,
};

// ─── HTML rendering helpers ───────────────────────────────────────────────────

const emailWrapper = (bodyHtml, footer) =>
  `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#fdf7f4;` +
  `font-family:Arial,sans-serif;">` +
  `<table width="100%" cellpadding="0" cellspacing="0">` +
  `<tr><td align="center" style="padding:32px 16px;">` +
  `<table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;` +
  `border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,0.08);overflow:hidden;">` +
  `<tr><td style="background:${BRAND};padding:20px 32px;">` +
  `<span style="color:#ffffff;font-size:18px;font-weight:bold;">${SHOP.name}</span>` +
  `</td></tr>` +
  `<tr><td style="padding:32px;">${bodyHtml}</td></tr>` +
  `<tr><td style="padding:16px 32px;border-top:1px solid #e8d8d0;` +
  `font-size:12px;color:#a0aec0;text-align:center;">` +
  `${footer || `© ${SHOP.name} · ${SHOP.email}`}` +
  `</td></tr>` +
  `</table></td></tr></table></body></html>`;

const buildDeliveryEmailHtml = (bodyText, name, orderId, reviewLinkUrl,
    reviewLinkText, footer) => {
  const safeUrl = reviewLinkUrl || DELIVERY_EMAIL_DEFAULTS.reviewLinkUrl;
  const safeText = reviewLinkText || DELIVERY_EMAIL_DEFAULTS.reviewLinkText;
  const reviewBtn =
    `<p style="text-align:center;margin:20px 0;">` +
    `<a href="${safeUrl}" style="background:${BRAND};color:#ffffff;` +
    `padding:12px 28px;border-radius:6px;text-decoration:none;` +
    `font-weight:bold;font-size:15px;display:inline-block;">${safeText}</a></p>`;
  const reviewLink =
    `<a href="${safeUrl}" style="color:${BRAND};font-weight:bold;">${safeText}</a>`;

  const lines = bodyText
    .replace(/\{\{NAME\}\}/g, name || "Kunde")
    .replace(/\{\{ORDER\}\}/g, orderId || "")
    .split("\n");

  const parts = lines.map((line) => {
    if (line.trim() === "{{REVIEW_LINK}}") return reviewBtn;
    if (line.trim() === "") return `<div style="height:10px;"></div>`;
    if (line.includes("{{REVIEW_LINK}}")) {
      const rendered = line.replace(/\{\{REVIEW_LINK\}\}/g, reviewLink);
      return (
        `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">` +
        `${rendered}</p>`
      );
    }
    return `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">${line}</p>`;
  });
  return emailWrapper(parts.join(""), footer);
};

const SAMPLE_LOCATION =
  "Sendung liegt zur Abholung bereit. Abholort: DHL Filiale, " +
  "Musterstraße 1, 12345 Musterstadt";

const buildPickupEmailHtml = (bodyText, name, orderId, location, footer) => {
  const locationBox =
    `<div style="background:#fff8e1;border-left:4px solid #f6ad55;` +
    `border-radius:4px;padding:12px 16px;margin:16px 0;` +
    `color:#744210;font-size:0.9rem;line-height:1.5;">` +
    `${location || SAMPLE_LOCATION}</div>`;

  const lines = bodyText
    .replace(/\{\{NAME\}\}/g, name || "Kunde")
    .replace(/\{\{ORDER\}\}/g, orderId || "")
    .split("\n");

  const parts = lines.map((line) => {
    if (line.trim() === "{{LOCATION}}") return locationBox;
    if (line.trim() === "") return `<div style="height:10px;"></div>`;
    return `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">${line}</p>`;
  });
  return emailWrapper(parts.join(""), footer);
};

// ─── Shared input/textarea styles ─────────────────────────────────────────────

const labelStyle = {
  display: "block", fontWeight: "600", fontSize: "0.78rem",
  color: "#4a5568", marginBottom: "0.3em",
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "0.5em 0.75em", border: "1px solid #e8d8d0",
  borderRadius: "6px", fontSize: "0.875rem",
  color: "#2d3748", background: "#fff",
  fontFamily: "inherit", marginBottom: "1.1em",
};
const taStyle = { ...inputStyle, resize: "vertical" };

// ─── Sub-component: Zugestellt tab ────────────────────────────────────────────

const DeliveryTab = () => {
  const [editMode,    setEditMode]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [form,        setForm]        = useState(null);
  const [testEmail,   setTestEmail]   = useState("tobias.boldt@gmail.com");
  const [testSending, setTestSending] = useState(false);
  const [testResult,  setTestResult]  = useState(null);
  const [firstName,   setFirstName]   = useState("Maria");
  const [orderNumber, setOrderNumber] = useState("4007861969");

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, "config", "deliveryEmailTexts"));
      setForm(snap.exists()
        ? { ...DELIVERY_EMAIL_DEFAULTS, ...snap.data() }
        : { ...DELIVERY_EMAIL_DEFAULTS });
    };
    load();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "deliveryEmailTexts"), form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(false);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade E-Mail-Konfiguration…
      </p>
    );
  }

  if (editMode) {
    return (
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#ebf8ff", border: "1px solid #90cdf4",
          borderRadius: "8px", padding: "0.75em 1.25em",
          marginBottom: "1.5em", gap: "1em",
        }}>
          <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
            ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-E-Mails aus.
          </span>
          <button onClick={() => setEditMode(false)} style={{
            padding: "0.35em 0.9em", borderRadius: "6px",
            border: "1px solid #90cdf4", background: "#fff",
            color: "#2c5282", fontSize: "0.8rem", fontWeight: "600",
            cursor: "pointer",
          }}>
            ← Vorschau
          </button>
        </div>

        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Zustellung-E-Mail bearbeiten
          </h3>

          <div style={{
            background: "#fdf7f4", border: "1px solid #e8d8d0",
            borderRadius: "8px", padding: "1.1em 1.25em", marginBottom: "1.75em",
          }}>
            <p style={{
              margin: "0 0 0.75em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              Globale Einstellungen
            </p>

            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Absender-Adresse
            </label>
            <div style={{ display: "flex", alignItems: "center",
              gap: "0.4em", marginBottom: "1em" }}>
              <input
                style={{ ...inputStyle, width: "160px", marginBottom: 0 }}
                value={form.fromLocal || ""}
                onChange={(e) => setField("fromLocal", e.target.value)}
                placeholder="versand"
              />
              <span style={{ color: "#4a5568", fontSize: "0.875rem",
                whiteSpace: "nowrap" }}>
                @{SHOP.email.split("@")[1]}
              </span>
            </div>

            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              E-Mail-Footer
            </label>
            <input
              style={{ ...inputStyle, marginBottom: "1em" }}
              value={form.emailFooter || ""}
              onChange={(e) => setField("emailFooter", e.target.value)}
            />

            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Bewertungs-Link URL
            </label>
            <input
              style={{ ...inputStyle, marginBottom: "1em" }}
              value={form.reviewLinkUrl || ""}
              onChange={(e) => setField("reviewLinkUrl", e.target.value)}
              placeholder="https://www.etsy.com/your/purchases"
            />

            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Bewertungs-Link Text (Button-Beschriftung)
            </label>
            <input
              style={{ ...inputStyle, marginBottom: 0 }}
              value={form.reviewLinkText || ""}
              onChange={(e) => setField("reviewLinkText", e.target.value)}
              placeholder="Bewertung hinterlassen →"
            />
          </div>

          <label style={labelStyle}>
            Betreff — Platzhalter: {"{{ORDER}}"}
          </label>
          <input
            style={inputStyle}
            value={form.subject || ""}
            onChange={(e) => setField("subject", e.target.value)}
          />

          <label style={labelStyle}>
            Text — Platzhalter: {"{{NAME}}"}, {"{{ORDER}}"}, {"{{REVIEW_LINK}}"}
          </label>
          <textarea
            style={{ ...taStyle, minHeight: "300px" }}
            value={form.body || ""}
            onChange={(e) => setField("body", e.target.value)}
          />

          <button onClick={handleSave} disabled={saving} style={{
            padding: "0.65em 2em", borderRadius: "7px",
            border: "none", background: saving ? "#a0aec0" : BRAND,
            color: "#fff", fontWeight: "700", fontSize: "0.95rem",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </div>
      </div>
    );
  }

  const handleSendTest = async () => {
    if (!testEmail) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-west1"), "sendTestDeliveryEmail");
      await fn({ to: testEmail, orderId: orderNumber, name: firstName });
      setTestResult("ok");
      setTimeout(() => setTestResult(null), 4000);
    } catch (err) {
      setTestResult("error");
      alert("Fehler beim Senden: " + err.message);
    } finally {
      setTestSending(false);
    }
  };

  const previewSubject = (form.subject || "")
    .replace(/\{\{ORDER\}\}/g, orderNumber);

  const previewHtml = buildDeliveryEmailHtml(
    form.body || "",
    firstName,
    orderNumber,
    form.reviewLinkUrl,
    form.reviewLinkText,
    form.emailFooter,
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "1em", gap: "1em",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Zustellung-E-Mail Vorschau
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75em" }}>
          {saved && (
            <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
              ✓ Gespeichert!
            </span>
          )}
          <button onClick={() => setEditMode(true)} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #e8d8d0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
            cursor: "pointer",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
      </div>

      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "1em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "1.25em", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Vorschau-Name
          </label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{ ...inputStyle, width: "140px", marginBottom: 0 }}
            placeholder="z.B. Maria"
          />
        </div>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Bestellnummer
          </label>
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            style={{ ...inputStyle, width: "150px", marginBottom: 0 }}
            placeholder="z.B. 4007861969"
          />
        </div>
      </div>

      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "0.9em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "0.75em", alignItems: "center", flexWrap: "wrap",
      }}>
        <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#4a5568",
          textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
          Test-E-Mail senden
        </span>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="test@example.com"
          style={{
            flex: 1, minWidth: "200px", padding: "0.4em 0.75em",
            border: "1px solid #e8d8d0", borderRadius: "6px",
            fontSize: "0.875rem", color: "#2d3748",
          }}
        />
        <button
          onClick={handleSendTest}
          disabled={testSending || !testEmail}
          style={{
            padding: "0.4em 1.1em", borderRadius: "6px", border: "none",
            background: testSending || !testEmail ? "#e2e8f0" : BRAND,
            color: testSending || !testEmail ? "#a0aec0" : "#fff",
            fontWeight: "600", fontSize: "0.875rem",
            cursor: testSending || !testEmail ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {testSending ? "Sende…" : "Jetzt senden"}
        </button>
        {testResult === "ok" && (
          <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
            ✓ Gesendet!
          </span>
        )}
      </div>

      <div style={{
        background: "#fff", borderRadius: "8px 8px 0 0",
        border: "1px solid #e8d8d0", borderBottom: "none",
        padding: "0.75em 1.25em", fontSize: "0.875rem", color: "#4a5568",
      }}>
        <span style={{ fontWeight: "600", color: "#2d3748" }}>Betreff: </span>
        {previewSubject}
      </div>

      <iframe
        key={`${firstName}-${orderNumber}-${JSON.stringify(form.body)}`}
        srcDoc={previewHtml}
        sandbox="allow-same-origin"
        style={{
          width: "100%", height: "620px",
          border: "1px solid #e8d8d0", borderRadius: "0 0 8px 8px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", display: "block",
        }}
        title="Zustellung-E-Mail Vorschau"
      />
    </div>
  );
};

// ─── Sub-component: Abholbereit tab ───────────────────────────────────────────

const PickupTab = () => {
  const [editMode,      setEditMode]      = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [form,          setForm]          = useState(null);
  const [testEmail,     setTestEmail]     = useState("tobias.boldt@gmail.com");
  const [testSending,   setTestSending]   = useState(false);
  const [testResult,    setTestResult]    = useState(null);
  const [firstName,     setFirstName]     = useState("Maria");
  const [orderNumber,   setOrderNumber]   = useState("4007861969");
  const [previewType,   setPreviewType]   = useState("initial"); // "initial" | "reminder"

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, "config", "pickupEmailTexts"));
      setForm(snap.exists()
        ? { ...PICKUP_EMAIL_DEFAULTS, ...snap.data() }
        : { ...PICKUP_EMAIL_DEFAULTS });
    };
    load();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "pickupEmailTexts"), form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(false);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade E-Mail-Konfiguration…
      </p>
    );
  }

  if (editMode) {
    return (
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#ebf8ff", border: "1px solid #90cdf4",
          borderRadius: "8px", padding: "0.75em 1.25em",
          marginBottom: "1.5em", gap: "1em",
        }}>
          <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
            ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-E-Mails aus.
          </span>
          <button onClick={() => setEditMode(false)} style={{
            padding: "0.35em 0.9em", borderRadius: "6px",
            border: "1px solid #90cdf4", background: "#fff",
            color: "#2c5282", fontSize: "0.8rem", fontWeight: "600",
            cursor: "pointer",
          }}>
            ← Vorschau
          </button>
        </div>

        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Abholbereit-E-Mails bearbeiten
          </h3>

          {/* Reminder delay */}
          <div style={{
            background: "#fdf7f4", border: "1px solid #e8d8d0",
            borderRadius: "8px", padding: "1.1em 1.25em", marginBottom: "1.75em",
          }}>
            <p style={{
              margin: "0 0 0.75em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              Erinnerung
            </p>
            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Erinnerungs-E-Mail senden nach (Tage)
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
              <input
                type="number"
                min="1"
                max="7"
                style={{ ...inputStyle, width: "80px", marginBottom: 0 }}
                value={form.reminderDays ?? 4}
                onChange={(e) =>
                  setField("reminderDays", parseInt(e.target.value, 10) || 4)}
              />
              <span style={{ color: "#4a5568", fontSize: "0.875rem" }}>
                Tage nach der ersten Abholbenachrichtigung
              </span>
            </div>
            <p style={{
              margin: "0.75em 0 0", fontSize: "0.78rem", color: "#718096",
            }}>
              DHL hält Pakete i.d.R. 7 Werktage. Empfehlung: 4 Tage.
            </p>
          </div>

          {/* Initial email */}
          <div style={{
            border: "1px solid #e8d8d0", borderRadius: "8px",
            padding: "1.25em", marginBottom: "1.75em",
          }}>
            <p style={{
              margin: "0 0 1em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              Erste E-Mail (sofort bei Abholbereit)
            </p>

            <label style={labelStyle}>
              Betreff — Platzhalter: {"{{ORDER}}"}
            </label>
            <input
              style={inputStyle}
              value={form.subject || ""}
              onChange={(e) => setField("subject", e.target.value)}
            />

            <label style={labelStyle}>
              Text — Platzhalter: {"{{NAME}}"}, {"{{ORDER}}"}, {"{{LOCATION}}"}
            </label>
            <p style={{ margin: "-0.6em 0 0.6em", fontSize: "0.78rem", color: "#718096" }}>
              {"{{LOCATION}}"} wird durch den DHL-Statustext mit Filialadresse ersetzt.
            </p>
            <textarea
              style={{ ...taStyle, minHeight: "260px" }}
              value={form.body || ""}
              onChange={(e) => setField("body", e.target.value)}
            />
          </div>

          {/* Reminder email */}
          <div style={{
            border: "1px solid #e8d8d0", borderRadius: "8px",
            padding: "1.25em", marginBottom: "1.75em",
          }}>
            <p style={{
              margin: "0 0 1em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              Erinnerungs-E-Mail (nach {form.reminderDays ?? 4} Tagen)
            </p>

            <label style={labelStyle}>
              Betreff — Platzhalter: {"{{ORDER}}"}
            </label>
            <input
              style={inputStyle}
              value={form.reminderSubject || ""}
              onChange={(e) => setField("reminderSubject", e.target.value)}
            />

            <label style={labelStyle}>
              Text — Platzhalter: {"{{NAME}}"}, {"{{ORDER}}"}, {"{{LOCATION}}"}
            </label>
            <textarea
              style={{ ...taStyle, minHeight: "260px" }}
              value={form.reminderBody || ""}
              onChange={(e) => setField("reminderBody", e.target.value)}
            />
          </div>

          <button onClick={handleSave} disabled={saving} style={{
            padding: "0.65em 2em", borderRadius: "7px",
            border: "none", background: saving ? "#a0aec0" : BRAND,
            color: "#fff", fontWeight: "700", fontSize: "0.95rem",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </div>
      </div>
    );
  }

  const handleSendTest = async () => {
    if (!testEmail) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-west1"), "sendTestPickupEmail");
      await fn({
        to: testEmail,
        orderId: orderNumber,
        name: firstName,
        emailType: previewType,
      });
      setTestResult("ok");
      setTimeout(() => setTestResult(null), 4000);
    } catch (err) {
      setTestResult("error");
      alert("Fehler beim Senden: " + err.message);
    } finally {
      setTestSending(false);
    }
  };

  const isReminder = previewType === "reminder";
  const subjectTpl = isReminder ?
    (form.reminderSubject || PICKUP_EMAIL_DEFAULTS.reminderSubject) :
    (form.subject || PICKUP_EMAIL_DEFAULTS.subject);
  const bodyTpl = isReminder ?
    (form.reminderBody || PICKUP_EMAIL_DEFAULTS.reminderBody) :
    (form.body || PICKUP_EMAIL_DEFAULTS.body);

  const previewSubject = subjectTpl.replace(/\{\{ORDER\}\}/g, orderNumber);
  const previewHtml = buildPickupEmailHtml(
    bodyTpl, firstName, orderNumber, SAMPLE_LOCATION,
    // footer inherited from delivery email config (shown as default here)
    "© " + SHOP.name + " · " + SHOP.email,
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "1em", gap: "1em",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Abholbereit-E-Mail Vorschau
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75em" }}>
          {saved && (
            <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
              ✓ Gespeichert!
            </span>
          )}
          <button onClick={() => setEditMode(true)} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #e8d8d0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
            cursor: "pointer",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
      </div>

      {/* Preview type toggle */}
      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "1em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "1.25em", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            E-Mail-Typ
          </label>
          <div style={{ display: "flex", gap: "0.5em" }}>
            {[
              { key: "initial", label: "Erste E-Mail" },
              { key: "reminder", label: `Erinnerung (nach ${form.reminderDays ?? 4}d)` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setPreviewType(key)}
                style={{
                  padding: "0.35em 0.9em", borderRadius: "6px", cursor: "pointer",
                  border: `1px solid ${previewType === key ? BRAND : "#e8d8d0"}`,
                  background: previewType === key ? BRAND : "#fff",
                  color: previewType === key ? "#fff" : "#4a5568",
                  fontWeight: "600", fontSize: "0.8rem",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Vorschau-Name
          </label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{ ...inputStyle, width: "140px", marginBottom: 0 }}
            placeholder="z.B. Maria"
          />
        </div>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Bestellnummer
          </label>
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            style={{ ...inputStyle, width: "150px", marginBottom: 0 }}
            placeholder="z.B. 4007861969"
          />
        </div>
      </div>

      {/* Test email sender */}
      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "0.9em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "0.75em", alignItems: "center", flexWrap: "wrap",
      }}>
        <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#4a5568",
          textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
          Test-E-Mail senden
        </span>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="test@example.com"
          style={{
            flex: 1, minWidth: "200px", padding: "0.4em 0.75em",
            border: "1px solid #e8d8d0", borderRadius: "6px",
            fontSize: "0.875rem", color: "#2d3748",
          }}
        />
        <button
          onClick={handleSendTest}
          disabled={testSending || !testEmail}
          style={{
            padding: "0.4em 1.1em", borderRadius: "6px", border: "none",
            background: testSending || !testEmail ? "#e2e8f0" : BRAND,
            color: testSending || !testEmail ? "#a0aec0" : "#fff",
            fontWeight: "600", fontSize: "0.875rem",
            cursor: testSending || !testEmail ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {testSending ? "Sende…" : `${isReminder ? "Erinnerung" : "Erste E-Mail"} senden`}
        </button>
        {testResult === "ok" && (
          <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
            ✓ Gesendet!
          </span>
        )}
      </div>

      {/* Subject line */}
      <div style={{
        background: "#fff", borderRadius: "8px 8px 0 0",
        border: "1px solid #e8d8d0", borderBottom: "none",
        padding: "0.75em 1.25em", fontSize: "0.875rem", color: "#4a5568",
      }}>
        <span style={{ fontWeight: "600", color: "#2d3748" }}>Betreff: </span>
        {previewSubject}
      </div>

      <iframe
        key={`${firstName}-${orderNumber}-${previewType}-${JSON.stringify(bodyTpl)}`}
        srcDoc={previewHtml}
        sandbox="allow-same-origin"
        style={{
          width: "100%", height: "620px",
          border: "1px solid #e8d8d0", borderRadius: "0 0 8px 8px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", display: "block",
        }}
        title="Abholbereit-E-Mail Vorschau"
      />
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DeliveryEmailPreview = () => {
  const [activeTab, setActiveTab] = useState("delivery");

  const tabs = [
    { key: "delivery", label: "Zugestellt" },
    { key: "pickup",   label: "Abholbereit" },
  ];

  return (
    <div>
      {/* Tab bar */}
      <div style={{
        display: "flex", gap: "0.25em",
        borderBottom: "2px solid #e8d8d0", marginBottom: "1.5em",
      }}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: "0.55em 1.25em", border: "none",
              borderBottom: activeTab === key ? `2px solid ${BRAND}` : "2px solid transparent",
              marginBottom: "-2px",
              background: "none",
              color: activeTab === key ? BRAND : "#718096",
              fontWeight: activeTab === key ? "700" : "500",
              fontSize: "0.9rem", cursor: "pointer",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "delivery" ? <DeliveryTab /> : <PickupTab />}
    </div>
  );
};

export default DeliveryEmailPreview;
